import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, CreditCard, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { BRL, ScopeToggle } from "./shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = {
  em_dia: { label: "Em dia", className: "bg-primary/10 text-primary", Icon: CheckCircle2 },
  atrasada: { label: "Atrasada", className: "bg-secondary/10 text-secondary", Icon: AlertTriangle },
  paga: { label: "Paga", className: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
};

const empty = (scope) => ({
  scope, name: "", total_amount: "", installment_amount: "", interest_rate: "",
  due_date: new Date().toISOString().slice(0, 10), status: "em_dia",
});

export default function Debts({ scope, setScope }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty(scope));
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get(`/debts?scope=${scope}`).then(({ data }) => setItems(data));
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty(scope)); setEditId(null); setOpen(true); };
  const openEdit = (d) => {
    setForm({ ...d, total_amount: String(d.total_amount), installment_amount: String(d.installment_amount || ""), interest_rate: String(d.interest_rate || "") });
    setEditId(d.id); setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form, scope,
        total_amount: parseFloat(form.total_amount),
        installment_amount: parseFloat(form.installment_amount || 0),
        interest_rate: parseFloat(form.interest_rate || 0),
      };
      if (!form.name.trim()) return toast.error("Informe o nome da dívida/credor");
      if (!payload.total_amount || payload.total_amount <= 0) return toast.error("Informe um valor total válido");
      if (editId) await api.put(`/debts/${editId}`, payload);
      else await api.post("/debts", payload);
      toast.success(editId ? "Dívida atualizada" : "Dívida cadastrada");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/debts/${id}`); toast.success("Dívida excluída"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totalActive = items.filter((d) => d.status !== "paga").reduce((a, d) => a + d.total_amount, 0);
  const lateCount = items.filter((d) => d.status === "atrasada").length;

  return (
    <div className="space-y-8" data-testid="debts-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Dívidas</h2>
          <p className="text-text-secondary font-body mt-1">Cadastre e acompanhe suas dívidas ativas.</p>
        </div>
        <div className="flex items-center gap-3">
          <ScopeToggle scope={scope} setScope={setScope} />
          <Button data-testid="add-debt-btn" onClick={openNew} className="rounded-md bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-1" /> Nova
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-surface border border-border rounded-2xl p-5" data-testid="debts-total-active">
          <p className="text-xs text-text-muted font-body uppercase tracking-wide">Total em dívidas ativas</p>
          <p className="font-heading text-2xl font-bold text-secondary mt-1">{BRL(totalActive)}</p>
        </div>
        <div className="bg-surface border border-border rounded-2xl p-5" data-testid="debts-late-count">
          <p className="text-xs text-text-muted font-body uppercase tracking-wide">Dívidas atrasadas</p>
          <p className="font-heading text-2xl font-bold text-text-primary mt-1">{lateCount}</p>
        </div>
      </div>

      <div className="border border-stone-200 bg-white rounded-md overflow-hidden">
        {items.length === 0 && <p className="p-8 text-center text-text-muted font-body">Nenhuma dívida cadastrada.</p>}
        {items.map((d) => {
          const st = STATUS[d.status] || STATUS.em_dia;
          return (
            <div key={d.id} data-testid={`debt-row-${d.id}`}
              className="flex items-center gap-4 px-6 py-4 border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors">
              <CreditCard className="h-8 w-8 text-text-muted shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-body font-medium text-text-primary truncate">{d.name}</p>
                <p className="text-sm text-text-muted font-body">
                  Parcela {BRL(d.installment_amount)} · {d.interest_rate}% a.m. · vence {d.due_date}
                </p>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-body font-medium ${st.className}`}>
                <st.Icon className="h-3.5 w-3.5" /> {st.label}
              </span>
              <p className="font-heading font-bold text-text-primary w-28 text-right">{BRL(d.total_amount)}</p>
              <div className="flex gap-1">
                <button data-testid={`edit-debt-${d.id}`} onClick={() => openEdit(d)} className="p-2 rounded hover:bg-stone-200 text-text-secondary">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`delete-debt-${d.id}`} onClick={() => remove(d.id)} className="p-2 rounded hover:bg-red-50 text-secondary">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md" data-testid="debt-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar" : "Nova"} dívida</DialogTitle>
            <DialogDescription className="font-body">Preencha os dados da dívida {scope === "business" ? "empresarial" : "pessoal"}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-body">Nome do credor/dívida</Label>
              <Input data-testid="debt-name" className="rounded-md" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Cartão Nubank" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-body">Valor total (R$)</Label>
                <Input data-testid="debt-total" type="number" step="0.01" className="rounded-md" value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })} placeholder="0,00" />
              </div>
              <div className="space-y-2">
                <Label className="font-body">Parcela (R$)</Label>
                <Input data-testid="debt-installment" type="number" step="0.01" className="rounded-md" value={form.installment_amount}
                  onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} placeholder="0,00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-body">Juros (% a.m.)</Label>
                <Input data-testid="debt-interest" type="number" step="0.01" className="rounded-md" value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} placeholder="0,00" />
              </div>
              <div className="space-y-2">
                <Label className="font-body">Vencimento</Label>
                <Input data-testid="debt-due-date" type="date" className="rounded-md" value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="debt-status" className="rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="em_dia">Em dia</SelectItem>
                  <SelectItem value="atrasada">Atrasada</SelectItem>
                  <SelectItem value="paga">Paga</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-debt" onClick={save} className="rounded-md bg-primary hover:bg-primary/90">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
